import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsBoxArrowUpRight } from 'react-icons/bs';

import TransferContactsToCrmModal from '#crmFeature/components/leads/TransferContactsToCrmModal/TransferContactsToCrmModal';
import TransferLeadsToCrmModal from '#crmFeature/components/leads/TransferLeadsToCrmModal/TransferLeadsToCrmModal';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import BulkActionsBar from '#globalComponents/Table/actions/BulkActionsBar';
import Table from '#globalComponents/Table/Table';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { CONTACT_ENTITY_LABEL } from '#lib/constants/entityLabels';
import { describeActionScope } from '#lib/describeActionScope';

import { hasActiveLeadFilters } from '../../../constants/leadFilters';
import { defaultDirectionForKey } from '../../../constants/leadSort';
import { useLeadData } from '../../../contexts/LeadDataContext';
import { useLeadJobs } from '../../../contexts/LeadJobContext';
import { useLeadUI } from '../../../contexts/LeadUIContext';
import { useActionScope } from '../../../hooks/useActionScope';
import { useTargetingVocabulary } from '../../../models/targeting/queries';
import { useLeadUIStore } from '../../../stores/leadUIStore';
import { isRunningSearchJob } from '../../../utils/isRunningSearchJob';
import { toLeadTableScope } from '../../../utils/toLeadTableScope';
import { hasLockedPhone } from '../../ContactPhone/ContactPhone';

import ContactExpandRow from './ContactExpandRow';
import { buildLeadColumns } from './leadColumns';
import LeadScopeActions from './LeadScopeActions';

export const LeadView = ({ leads, isLoading, orderBy, orderDir, onSortChange }) => {
	const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
	const [showTransferToCrmModal, setShowTransferToCrmModal] = useState(false);
	const [showTransferContactsToCrmModal, setShowTransferContactsToCrmModal] = useState(false);
	const [expandedLeads, setExpandedLeads] = useState(new Set());
	const { hasCapability: canUseCrm } = useCapabilityCheck('crm.access');
	const { data: targetingVocabulary } = useTargetingVocabulary();
	const navigate = useNavigate();
	const {
		contactMap,
		highlightId,
		clearHighlight,
		loadingLeads,
		filters,
		handleFilterChange,
		currentPage,
		totalPages,
		totalCount,
		pageSize,
		handlePageChange,
	} = useLeadData();
	// How many numbers the org could unlock across the whole list — the teaser's strongest argument.
	const lockedPhoneCount = useMemo(
		() => Object.values(contactMap).flat().filter(hasLockedPhone).length,
		[contactMap],
	);
	const {
		selectAllInFilter,
		hasContactSelection,
		leadSelectionCount,
		leadActionScopeLabel,
		leadEntityLabel,
		leadTableSelection,
		clearLeadSelection,
		selectedContacts,
		handleContactToggle,
		clearContactSelection,
	} = useLeadUI();
	const actionScope = useActionScope();
	const { handleDeleteLeads, runningJobs } = useLeadJobs();
	const setShowEmailListModal = useLeadUIStore((s) => s.setShowEmailListModal);

	// Scroll to a search hit once its page has rendered, then let the ring fade by
	// clearing the highlight param. A stale id (row no longer on this page) is
	// cleared silently.
	const scrolledHighlightRef = useRef(null);
	useEffect(() => {
		if (!highlightId || loadingLeads) return;
		if (!leads.some((lead) => lead.id === highlightId)) {
			clearHighlight();
			return;
		}
		if (scrolledHighlightRef.current !== highlightId) {
			scrolledHighlightRef.current = highlightId;
			requestAnimationFrame(() => {
				document
					.getElementById(`lead-row-${highlightId}`)
					?.scrollIntoView({ block: 'center', behavior: 'smooth' });
			});
		}
		const timer = setTimeout(clearHighlight, 2500);
		return () => clearTimeout(timer);
	}, [highlightId, loadingLeads, leads, clearHighlight]);

	const handleToggleExpand = useCallback((leadId) => {
		setExpandedLeads((prev) => {
			const next = new Set(prev);
			if (next.has(leadId)) {
				next.delete(leadId);
			} else {
				next.add(leadId);
			}
			return next;
		});
	}, []);

	const handleDeleteClick = () => {
		setShowDeleteConfirmModal(true);
	};

	const handleConfirmDelete = () => {
		handleDeleteLeads({ prospectQueryId: actionScope.prospectQueryId, scope: actionScope.scope });
		clearLeadSelection();
		setShowDeleteConfirmModal(false);
	};

	const [rowToDelete, setRowToDelete] = useState(null);
	const handleDeleteRow = useCallback((lead) => setRowToDelete(lead), []);
	const handleConfirmDeleteRow = () => {
		handleDeleteLeads({ prospectQueryId: rowToDelete.prospectQueryId, scope: { leadIds: [rowToDelete.id] } });
		setRowToDelete(null);
	};

	const handleCancelDelete = () => {
		setShowDeleteConfirmModal(false);
	};

	// Sorting is server-side across all pages. Table reports only the clicked
	// key, so the direction is derived here: toggle the active column, otherwise
	// start the new one in its natural direction.
	const handleSortKeyChange = useCallback(
		(sortKey) => {
			if (orderBy === sortKey) {
				onSortChange(sortKey, orderDir === 'asc' ? 'desc' : 'asc');
			} else {
				onSortChange(sortKey, defaultDirectionForKey(sortKey));
			}
		},
		[orderBy, orderDir, onSortChange],
	);

	const columns = useMemo(
		() =>
			buildLeadColumns({
				filters,
				onFilterChange: handleFilterChange,
				expandedLeads,
				onToggleExpand: handleToggleExpand,
				onNameClick: (leadId) => navigate(`/leads/company/${leadId}`),
				onDelete: handleDeleteRow,
				vocabulary: targetingVocabulary,
			}),
		[
			filters,
			handleFilterChange,
			expandedLeads,
			handleToggleExpand,
			navigate,
			handleDeleteRow,
			targetingVocabulary,
		],
	);

	const actionScopeLabel = hasContactSelection
		? describeActionScope({ count: selectedContacts.length, isSelection: true, ...CONTACT_ENTITY_LABEL })
		: leadActionScopeLabel;
	const deleteMessage = `Möchten Sie wirklich ${leadSelectionCount} ${leadSelectionCount === 1 ? 'Lead' : 'Leads'} löschen?${
		selectAllInFilter ? ' Das betrifft alle ausgewählten Firmen über alle Seiten hinweg.' : ''
	}`;

	const transferToCrmAction = (onClick) =>
		canUseCrm ? [{ key: 'transferToCrm', icon: <BsBoxArrowUpRight />, label: 'Ins CRM übertragen', onClick }] : [];

	return (
		<div className="relative flex-1 min-h-0 flex flex-col">
			<div data-tour="lead-table" className="isolate flex min-h-0 flex-col">
				<Table
					layoutKey="leads"
					rows={leads}
					columns={columns}
					isLoading={isLoading}
					highlightedId={highlightId ?? undefined}
					emptyLabel={
						hasActiveLeadFilters(filters)
							? 'Keine Firmen entsprechen den aktiven Filtern'
							: runningJobs.some(isRunningSearchJob)
								? 'Suche läuft – gefundene Firmen erscheinen hier automatisch.'
								: 'Keine Leads für diese Suchanfrage gefunden'
					}
					getRowProps={(lead) => ({
						id: `lead-row-${lead.id}`,
						className: lead.id === highlightId ? 'scroll-mt-24' : '',
					})}
					selection={leadTableSelection}
					actions={{ label: actionScopeLabel, buttons: <LeadScopeActions /> }}
					pagination={{
						currentPage,
						totalPages,
						onPageChange: handlePageChange,
						totalItems: totalCount,
						pageSize,
					}}
					sort={{ sortKey: orderBy, sortDir: orderDir, onSortChange: handleSortKeyChange }}
					expansion={{
						isExpanded: (id) => expandedLeads.has(id),
						renderExpanded: (lead, colSpan) => (
							<ContactExpandRow
								contacts={contactMap[lead.id] || []}
								companyEmail={lead.emailAddress}
								selectedContacts={selectedContacts}
								onContactToggle={handleContactToggle}
								colSpan={colSpan}
								lockedPhoneCount={lockedPhoneCount}
							/>
						),
					}}
					bulkBar={
						<>
							{/* Leads win over contacts (select-all lives in the table header checkbox). */}
							{hasContactSelection ? (
								<BulkActionsBar
									selectedCount={selectedContacts.length}
									entityLabel={CONTACT_ENTITY_LABEL}
									onClear={clearContactSelection}
									onAddToContactList={() => setShowEmailListModal(true)}
									actions={transferToCrmAction(() => setShowTransferContactsToCrmModal(true))}
								/>
							) : (
								<BulkActionsBar
									selectedCount={leadSelectionCount}
									entityLabel={leadEntityLabel}
									onClear={clearLeadSelection}
									onAddToContactList={() => setShowEmailListModal(true)}
									actions={transferToCrmAction(() => setShowTransferToCrmModal(true))}
									onDelete={handleDeleteClick}
								/>
							)}
						</>
					}
				/>
			</div>

			<ConfirmModal
				isOpen={showDeleteConfirmModal}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Leads löschen"
				message={deleteMessage}
				confirmText="Leads löschen"
				cancelText="Abbrechen"
			/>

			<ConfirmModal
				isOpen={Boolean(rowToDelete)}
				onClose={() => setRowToDelete(null)}
				onConfirm={handleConfirmDeleteRow}
				title="Lead löschen"
				message={`Möchten Sie „${rowToDelete?.companyName ?? 'diese Firma'}“ wirklich aus der Suchanfrage löschen?`}
				confirmText="Lead löschen"
			/>

			{canUseCrm && (
				<TransferLeadsToCrmModal
					isOpen={showTransferToCrmModal}
					onClose={() => setShowTransferToCrmModal(false)}
					tableScope={toLeadTableScope(actionScope)}
					count={actionScope.count}
				/>
			)}

			{/* Backend auto-imports the global contacts into the CRM and links only the
			    picked contacts to the new deal. */}
			{canUseCrm && (
				<TransferContactsToCrmModal
					isOpen={showTransferContactsToCrmModal}
					onClose={() => {
						setShowTransferContactsToCrmModal(false);
						clearContactSelection();
					}}
					contactIds={selectedContacts}
				/>
			)}
		</div>
	);
};

LeadView.propTypes = {
	leads: PropTypes.array.isRequired,
	isLoading: PropTypes.bool.isRequired,
	orderBy: PropTypes.string.isRequired,
	orderDir: PropTypes.oneOf(['asc', 'desc']).isRequired,
	onSortChange: PropTypes.func.isRequired,
};
